import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-scales-3-line',
    title: 'Seu escritório transmite a mesma autoridade dos seus argumentos',
    description: 'Mesa de diretoria imponente, cadeiras presidenciais em couro e acabamento em madeira nobre. O cliente entra na sala e já sente que está em boas mãos — antes de qualquer palavra ser dita.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Especialistas em escritórios de advocacia',
    description: 'Atendemos escritórios de pequeno, médio e grande porte — desde o advogado solo que precisa de uma sala impecável até bancas com 20 advogados e múltiplas salas de reunião. Cada projeto respeita a identidade da marca.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do seu escritório',
    description: 'Antes de fabricar qualquer peça, você aprova o projeto completo em 3D. Vê exatamente como vai ficar a sala de reunião com o cliente, a diretoria e a recepção. Ajustamos até ficar perfeito.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipe própria de montagem',
    description: 'Instalação feita pela nossa equipe, no horário que seu escritório preferir — inclusive após o expediente ou aos sábados. Sem terceiros, sem sujeira, sem interromper seus atendimentos.',
  },
];

const blocks = [
  {
    title: 'Sala de Reunião que Fecha Contratos Antes da Primeira Palavra',
    text: [
      'Um cliente entra na sua sala de reunião e em 7 segundos já formou uma opinião sobre seu escritório. A mesa de reunião, as cadeiras, a iluminação, o painel de madeira — tudo comunica poder, solidez e confiança. E confiança é o que fecha contratos de honorários altos.',
      'Nossas salas de reunião para escritórios de advocacia incluem mesa de reunião em MDF de alta espessura com tampo em vidro temperado fumê ou laminado premium imitando madeira nobre, cadeiras em couro legítimo ou courvin de alta qualidade com costura aparente, estrutura em alumínio escovado ou aço cromado, painel de madeira com iluminação LED embutida para diplomas e certificações, e iluminação direcionada que cria um ambiente sério e acolhedor ao mesmo tempo.',
    ],
    img: '/media/ia/0365.webp',
    reverse: false,
  },
  {
    title: 'Mesa de Diretoria que Comunica Poder e Experiência',
    text: [
      'A mesa do advogado é o centro do escritório. É onde ele recebe clientes, analisa processos, toma decisões estratégicas e assina contratos que valem milhões. Nossas mesas de diretoria são projetadas para transmitir autoridade desde o primeiro olhar.',
      'Utilizamos tampos em MDF de 50mm com acabamento em laca fosca ou brilhante, pés em aço inox escovado ou madeira maciça, gavetas com corrediças telescópicas e fechamento suave para guardar processos e documentos confidenciais, passa-fios integrado para organizar cabos de computador, impressora e telefone, e acabamento em couro legítimo na superfície de trabalho para conforto durante longas horas de análise de processos.',
    ],
    img: '/media/ia/0366.webp',
    reverse: true,
  },
  {
    title: 'Recepção Sofisticada que Impressiona do Primeiro Contato',
    text: [
      'A recepção do escritório de advocacia é onde o cliente forma a primeira impressão. Ela precisa ser acolhedora, mas também transmitir seriedade e competência. Nossos projetos de recepção para escritórios jurídicos equilibram conforto com autoridade.',
      'Projetamos balcão de recepção com iluminação LED embutida e logo do escritório em relevo metálico, sofás e poltronas de design em couro ou linho que convidam à espera confortável sem parecer casual demais, painéis de madeira com textura que transmitem solidez e tradição, mesa de apoio para revistas jurídicas e água, e piso vinílico ou carpete que absorve o som dos passos e cria um ambiente silencioso e profissional. Tudo para que o cliente se sinta respeitado desde o momento em que cruza a porta.',
    ],
    img: '/media/ia/0367.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0368.webp',
    alt: 'Mesa de diretoria para escritório de advocacia',
  },
  {
    src: '/media/ia/0369.webp',
    alt: 'Sala de reunião executiva para banca de advogados',
  },
  {
    src: '/media/ia/0370.webp',
    alt: 'Recepção sofisticada de escritório jurídico',
  },
  {
    src: '/media/ia/0371.webp',
    alt: 'Biblioteca e arquivo de escritório de advocacia',
  },
];

export default function AdvogadosPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Mobiliário para Escritório de Advocacia Caruaru PE | Home Office Premium',
    description: 'Mobiliário para escritório de advocacia em Caruaru PE. Mesas de diretoria, salas de reunião e recepções premium. Projeto 3D gratuito, montagem pela nossa equipe. Atendemos advogados e bancas jurídicas no Agreste PE.',
    keywords: 'mobiliário escritório advocacia Recife, mesa diretoria advogado Petrolina, sala reunião jurídica Garanhuns, móveis escritório jurídico Arcoverde, decoração escritório advocacia Sertão PE, mesa advogado Pernambuco, mobiliário banca jurídica Agreste, mesa diretoria advogado Nordeste, sala reunião escritório advocacia Recife, recepção escritório jurídico Petrolina, mobiliário advocacia Caruaru, mesa advogado Bezerros',
    path: '/advogados',
  });

  useSchemaLD({
    id: 'schema-advogados',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Mobiliário para Escritórios de Advocacia | Caruaru PE',
      description: 'Mesas de diretoria, salas de reunião e recepções premium para escritórios de advocacia. Projeto 3D gratuito, equipe própria de montagem.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/advogados`,
      about: {
        '@type': 'Service',
        name: 'Mobiliário para Escritórios de Advocacia',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      {/* Hero */}
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0372.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Escritórios de Advocacia e Bancas Jurídicas
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Seu escritório deve transmitir a mesma autoridade que seus argumentos jurídicos.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Mesas de diretoria imponentes, salas de reunião que fecham contratos e recepções que impressionam do primeiro contato. Porque um cliente que confia no seu ambiente, confia no seu trabalho.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Advocacia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que advogados e bancas jurídicas escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Blocks */}
      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes Jurídicos</span> que Fecham Contratos
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Advocacia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-gray-900 text-3xl md:text-4xl font-bold mb-8">
            Mais de <span className="text-[#E8610A]">10 anos</span> projetando escritórios que transmitem autoridade
          </h2>
          <p className="text-gray-600 text-base leading-relaxed mb-5">
            A <strong>Home Office Premium</strong> entende que para um advogado, o escritório é uma extensão da sua reputação. Atendemos bancas de advocacia em Caruaru, Recife e todo o Agreste Pernambucano — desde o advogado solo que precisa de uma sala impecável até escritórios com múltiplas salas de reunião e áreas de convivência.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-10">
            Nosso processo garante zero surpresas: visita técnica, projeto 3D para aprovação, fabricação com materiais de primeira linha e instalação pela nossa equipe — inclusive após o expediente ou aos sábados, para não interromper seus atendimentos. Você não precisa se preocupar com nada — só com seus processos.
          </p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Advocacia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center bg-[#E8610A] hover:bg-[#C4500A] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            FALAR COM UM ESPECIALISTA
          </a>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja seu escritório de advocacia em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Nossa equipe visita seu escritório, entende sua visão e entrega o projeto 3D completo em até 48h. Você aprova cada detalhe — da mesa de diretoria à recepção — antes de fabricarmos.
          </p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Advocacia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">
              Ambientes <span className="text-[#E8610A]">Jurídicos Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">
            SUPORTE LOCAL · CARUARU E AGRESTE PERNAMBUCANO
          </p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">
            Solicite seu orçamento
          </h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a
            href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Advocacia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm leading-relaxed">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" />
            </a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" />
            </a>
          </div>
        </div>
      </footer>

      <a
        ref={whatsappBtnRef}
        href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Advocacia%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200"
        aria-label="WhatsApp"
        style={{ display: 'flex' }}
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
